/**
 * ONE source for the NUMBERS in customer-facing copy (fees, load ceiling, age).
 *
 * Same discipline as `referral-terms.ts`: a figure is never typed into a
 * sentence. It is resolved at render from programme configuration, because a
 * typed number is right only until config moves — and a typed number that
 * happens to be right today is indistinguishable from a correct one.
 *
 * DERIVED FIGURES ARE DERIVED, NOT TYPED. "each extra" is not configured
 * anywhere: it is arithmetic over the configured single price and the configured
 * multi-card tier. When the tiers cannot support the subtraction, the sentence
 * renders NOTHING rather than a plausible guess.
 *
 * UNRESOLVED = NOT SHOWN. `resolveFigures()` returns null when a token in the
 * sentence cannot be resolved from config, and callers drop the sentence.
 */
import { useEffect, useState } from "react";
import { getPublicProgramBySlug, LAUNCH_PROGRAM_SLUG } from "@/services/programs";
import { getAgeGateConfig } from "@/services/age-gate";
import { resolveMaxLoadCents, resolvePricing } from "@/lib/consumer-config";

export interface ProgrammeFigures {
  /** Formatted single-card fee, e.g. "$30". Null when no fee is configured. */
  feeOne: string | null;
  /** Formatted DERIVED per-extra-card price. Null when it cannot be derived. */
  feeExtra: string | null;
  /** Formatted configured single-load ceiling, e.g. "$1,000". */
  loadLimit: string | null;
  /** Configured age threshold in years. */
  minAge: number | null;
}

function money(cents: number, currency: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "USD",
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

/**
 * The per-extra-card price, DERIVED: (multi-card tier price − single price)
 * divided by the extra cards in that tier. Null unless both are configured and
 * the arithmetic is sane.
 */
export function deriveExtraCardCents(
  singleCents: number | null,
  tiers: Array<{ quantity: number; priceCents: number }>,
): number | null {
  if (singleCents === null) return null;
  const multi = tiers
    .filter((t) => t.quantity > 1 && Number.isFinite(t.priceCents))
    .sort((a, b) => a.quantity - b.quantity)[0];
  if (!multi) return null;
  const extras = multi.quantity - 1;
  const delta = multi.priceCents - singleCents;
  if (extras <= 0 || delta <= 0 || delta % extras !== 0) return null;
  return delta / extras;
}

export async function fetchProgrammeFigures(slug?: string): Promise<ProgrammeFigures> {
  const [program, ageCfg] = await Promise.all([
    getPublicProgramBySlug(slug ?? LAUNCH_PROGRAM_SLUG),
    getAgeGateConfig(),
  ]);
  const cfg = (program.data?.config ?? {}) as Record<string, any>;
  const pricing = resolvePricing(cfg, program.data?.currency ?? null);
  const extra = deriveExtraCardCents(pricing.cardFeeCents, pricing.tiers);
  const limit = resolveMaxLoadCents(cfg);
  return {
    feeOne: pricing.cardFeeCents === null ? null : money(pricing.cardFeeCents, pricing.currency),
    feeExtra: extra === null ? null : money(extra, pricing.currency),
    loadLimit: limit === null ? null : money(limit, pricing.currency),
    minAge: Number.isFinite(ageCfg.thresholdYears) ? ageCfg.thresholdYears : null,
  };
}

/** Null until resolved. */
export function useProgrammeFigures(slug?: string): ProgrammeFigures | null {
  const [figures, setFigures] = useState<ProgrammeFigures | null>(null);
  useEffect(() => {
    let live = true;
    fetchProgrammeFigures(slug).then((f) => {
      if (live) setFigures(f);
    });
    return () => {
      live = false;
    };
  }, [slug]);
  return figures;
}

/**
 * Fills {feeOne} / {feeExtra} / {loadLimit} / {minAge} in a counsel-owned
 * sentence. Returns null when any token the sentence uses is unresolved — an
 * unresolved money, limit or age token must never reach a customer.
 */
export function resolveFigures(template: string, figures: ProgrammeFigures | null): string | null {
  if (!figures) return null;
  const map: Record<string, string | null> = {
    feeOne: figures.feeOne,
    feeExtra: figures.feeExtra,
    loadLimit: figures.loadLimit,
    minAge: figures.minAge === null ? null : String(figures.minAge),
  };
  let out = template;
  for (const [token, value] of Object.entries(map)) {
    if (!out.includes(`{${token}}`)) continue;
    if (value === null) return null;
    out = out.split(`{${token}}`).join(value);
  }
  return out;
}
